/**
 * Pinned Questions Collection
 *
 * Handles API operations for collections of pinned questions
 * Extends the base List<T> class following warbler pattern
 * Provides ordering, filtering, and analytics functionality
 */

import { get, post } from '@/services/http';
import { PinnedQuestionModel } from '@/models/pinned_question';
import {
  IFetchPinnedQuestionsRequest,
  IBulkReorderRequest,
  IPinnedQuestionsSummary,
  IPinnedQuota,
  QuestionCategory,
  QuestionPriority,
  PinReason,
} from '@/models/insights/';
import List from './list';

export class PinnedQuestionsList extends List<PinnedQuestionModel> {
  public total: number = 0;

  /**
   * Fetch pinned questions from API with filtering
   */
  public async fetch(options: IFetchPinnedQuestionsRequest = {}): Promise<any> {
    try {
      const queryParams = {
        priority: options.priority,
        category: options.category,
        pin_reason: options.pin_reason,
        limit: options.limit || 10,
        offset: options.offset || 0,
        include_analytics: options.include_analytics || true,
      };

      // Remove undefined values
      Object.keys(queryParams).forEach((key) =>
        queryParams[key as keyof typeof queryParams] === undefined && delete queryParams[key as keyof typeof queryParams],
      );

      const response: any = await get('/pinned-questions', queryParams);
      if (response && response.success) {
        this.mapData(response);
        return response;
      }
      throw new Error(response?.message || 'Failed to fetch pinned questions');
    } catch (error) {
      console.error('Error fetching pinned questions:', error);
      throw error;
    }
  }

  /**
   * Map API response data to collection items
   */
  public mapData(response: any): void {
    // Clear existing items
    this.items = [];
    this.total = response.pagination?.total || response.data?.length || 0;

    if (response.data && Array.isArray(response.data)) {
      for (const questionData of response.data) {
        const questionModel = new PinnedQuestionModel(questionData);
        this.add(questionModel);
      }

      // Sort by display_order to maintain proper ordering
      this.sortByOrderIndex();
    }
  }

  /**
   * Get pinned questions by priority
   */
  public async fetchByPriority(priority: QuestionPriority): Promise<any> {
    return this.fetch({ priority });
  }

  /**
   * Get pinned questions by category
   */
  public async fetchByCategory(category: QuestionCategory | string): Promise<any> {
    return this.fetch({ category });
  }

  /**
   * Get pinned questions by pin reason
   */
  public async fetchByPinReason(pinReason: PinReason | string): Promise<any> {
    return this.fetch({ pin_reason: pinReason });
  }

  /**
   * Get high priority pinned questions
   */
  public async fetchHighPriority(): Promise<any> {
    return this.fetch({ priority: QuestionPriority.HIGH });
  }

  /**
   * Get pinned questions summary with analytics
   */
  public async getSummary(): Promise<IPinnedQuestionsSummary> {
    try {
      const response: any = await get('/pinned-questions/summary');
      if (response && response.success) {
        return response.data;
      }
      throw new Error(response?.message || 'Failed to fetch summary');
    } catch (error) {
      console.error('Error fetching pinned questions summary:', error);
      throw error;
    }
  }

  /**
   * Get pinned questions quota information
   */
  public async getQuota(): Promise<IPinnedQuota> {
    try {
      const response: any = await get('/pinned-questions/quota');
      if (response && response.success) {
        return response.data;
      }
      throw new Error(response?.message || 'Failed to fetch quota');
    } catch (error) {
      console.error('Error fetching quota:', error);
      throw error;
    }
  }

  /**
   * Bulk reorder pinned questions
   */
  public async bulkReorder(orderUpdates: Array<{id: number, display_order: number}>): Promise<void> {
    try {
      const payload: IBulkReorderRequest = {
        order_updates: orderUpdates.map((update) => ({
          id: update.id,
          display_order: update.display_order,
        })),
      };

      await post('/pinned-questions/bulk-reorder', payload);

      // Update local order after successful API call
      this.updateLocalOrder(orderUpdates);

    } catch (error) {
      console.error('Error bulk reordering questions:', error);
      throw error;
    }
  }

  /**
   * Move question to new position
   */
  public async moveQuestion(questionId: number, newPosition: number): Promise<void> {
    const question = this.items.find((q) => q.id === questionId);
    if (!question) {
      throw new Error(`Question with ID ${questionId} not found`);
    }

    const currentPosition = question.display_order;
    if (currentPosition === newPosition) {
      return; // No change needed
    }

    // Calculate new order indices for affected items
    const orderUpdates: Array<{id: number, display_order: number}> = [];

    if (newPosition > currentPosition) {
      // Moving down: shift items up
      for (const item of this.items) {
        if (item.display_order > currentPosition && item.display_order <= newPosition) {
          orderUpdates.push({
            id: item.id,
            display_order: item.display_order - 1,
          });
        }
      }
    } else {
      // Moving up: shift items down
      for (const item of this.items) {
        if (item.display_order >= newPosition && item.display_order < currentPosition) {
          orderUpdates.push({
            id: item.id,
            display_order: item.display_order + 1,
          });
        }
      }
    }

    // Add the moved question
    orderUpdates.push({
      id: questionId,
      display_order: newPosition,
    });

    await this.bulkReorder(orderUpdates);
  }

  /**
   * Sort items by display_order
   */
  public sortByOrderIndex(): void {
    this.items.sort((a, b) => a.display_order - b.display_order);
  }

  /**
   * Filter local items by priority
   */
  public filterItemsByPriority(priority: QuestionPriority): PinnedQuestionModel[] {
    return this.items.filter((question) => question.priority === priority);
  }

  /**
   * Filter local items by category
   */
  public filterItemsByCategory(category: QuestionCategory | string): PinnedQuestionModel[] {
    return this.items.filter((question) => question.category === category);
  }

  /**
   * Filter local items by pin reason
   */
  public filterItemsByPinReason(pinReason: PinReason): PinnedQuestionModel[] {
    return this.items.filter((question) => question.pin_reason === pinReason);
  }

  /**
   * Get high priority items from local collection
   */
  public getHighPriorityItems(): PinnedQuestionModel[] {
    return this.filterItemsByPriority(QuestionPriority.HIGH);
  }

  /**
   * Get items grouped by category
   */
  public getGroupedByCategory(): Record<string, PinnedQuestionModel[]> {
    const grouped: Record<string, PinnedQuestionModel[]> = {};

    for (const question of this.items) {
      const category = question.category || (question as any).getCategoryFromMetadata();
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(question);
    }

    return grouped;
  }

  /**
   * Get items grouped by priority
   */
  public getGroupedByPriority(): Record<string, PinnedQuestionModel[]> {
    const grouped: Record<string, PinnedQuestionModel[]> = {};

    for (const question of this.items) {
      const priority = question.priority.toString();
      if (!grouped[priority]) {
        grouped[priority] = [];
      }
      grouped[priority].push(question);
    }

    return grouped;
  }

  /**
   * Get items grouped by pin reason
   */
  public getGroupedByPinReason(): Record<string, PinnedQuestionModel[]> {
    const grouped: Record<string, PinnedQuestionModel[]> = {};

    for (const question of this.items) {
      const reason = question.pin_reason || 'unknown';
      if (!grouped[reason]) {
        grouped[reason] = [];
      }
      grouped[reason].push(question);
    }

    return grouped;
  }

  /**
   * Search local items by question text
   */
  public searchItems(searchText: string): PinnedQuestionModel[] {
    const lowerSearchText = searchText.toLowerCase();
    return this.items.filter((question) =>
      question.question_text.toLowerCase().includes(lowerSearchText) ||
      (question.metadata?.tags || []).some((tag: string) => tag.toLowerCase().includes(lowerSearchText)),
    );
  }

  /**
   * Get items sorted by pinned date
   */
  public getSortedByPinnedDate(descending: boolean = true): PinnedQuestionModel[] {
    return [...this.items].sort((a, b) => {
      const dateA = a.pinned_at ? new Date(a.pinned_at).getTime() : 0;
      const dateB = b.pinned_at ? new Date(b.pinned_at).getTime() : 0;
      return descending ? dateB - dateA : dateA - dateB;
    });
  }

  /**
   * Get recently pinned items
   */
  public getRecentlyPinned(limit: number = 5): PinnedQuestionModel[] {
    return this.getSortedByPinnedDate(true).slice(0, limit);
  }

  /**
   * Check if collection is at capacity
   */
  public async isAtCapacity(): Promise<boolean> {
    try {
      const quota = await this.getQuota();
      return quota.used >= quota.limit;
    } catch (error) {
      console.error('Error checking capacity:', error);
      return false;
    }
  }

  /**
   * Get available slots for new pinned questions
   */
  public async getAvailableSlots(): Promise<number> {
    try {
      const quota = await this.getQuota();
      return quota.remaining;
    } catch (error) {
      console.error('Error getting available slots:', error);
      return 0;
    }
  }

  /**
   * Get next available order index
   */
  public getNextOrderIndex(): number {
    if (this.items.length === 0) {
      return 1;
    }

    const maxOrder = Math.max(...this.items.map((item) => item.display_order));
    return maxOrder + 1;
  }

  /**
   * Validate order indices consistency
   */
  public validateOrderIndices(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];
    const orderIndices = this.items.map((item) => item.display_order).sort((a, b) => a - b);

    // Check for duplicates
    const duplicates = orderIndices.filter((index, pos) => orderIndices.indexOf(index) !== pos);
    if (duplicates.length > 0) {
      issues.push(`Duplicate order indices found: ${duplicates.join(', ')}`);
    }

    // Check for gaps (should be sequential starting from 1)
    for (let i = 0; i < orderIndices.length; i++) {
      if (orderIndices[i] !== i + 1) {
        issues.push(`Order index gap detected at position ${i + 1}`);
        break;
      }
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Fix order indices to be sequential
   */
  public async fixOrderIndices(): Promise<void> {
    const orderUpdates: Array<{id: number, display_order: number}> = [];

    // Sort by current order and assign sequential indices
    this.sortByOrderIndex();

    for (let i = 0; i < this.items.length; i++) {
      const expectedOrder = i + 1;
      if (this.items[i].display_order !== expectedOrder) {
        orderUpdates.push({
          id: this.items[i].id,
          display_order: expectedOrder,
        });
      }
    }

    if (orderUpdates.length > 0) {
      await this.bulkReorder(orderUpdates);
    }
  }

  /**
   * Get local statistics about pinned questions
   */
  public getLocalStats(): {
    total: number;
    byCategory: Record<string, number>;
    byPriority: Record<string, number>;
    byPinReason: Record<string, number>;
    orderValidation: { isValid: boolean; issues: string[] };
  } {
    const byCategory: Record<string, number> = {};
    const byPriority: Record<string, number> = {};
    const byPinReason: Record<string, number> = {};

    for (const question of this.items) {
      // Count by category
      const category = question.category || (question as any).getCategoryFromMetadata();
      byCategory[category] = (byCategory[category] || 0) + 1;

      // Count by priority
      const priority = question.priority.toString();
      byPriority[priority] = (byPriority[priority] || 0) + 1;

      // Count by pin reason
      const reason = question.pin_reason || 'unknown';
      byPinReason[reason] = (byPinReason[reason] || 0) + 1;
    }

    return {
      total: this.items.length,
      byCategory,
      byPriority,
      byPinReason,
      orderValidation: this.validateOrderIndices(),
    };
  }

  /**
   * Reset collection state
   */
  public reset(): void {
    this.items = [];
    this.total = 0;
  }

  /**
   * Update local order after successful API reorder
   */
  private updateLocalOrder(orderUpdates: Array<{id: number, display_order: number}>): void {
    for (const update of orderUpdates) {
      const question = this.items.find((q) => q.id === update.id);
      if (question) {
        question.display_order = update.display_order;
      }
    }

    this.sortByOrderIndex();
  }
}
